import React, { ComponentType, ReactElement } from 'react'
import merge from 'lodash/merge'
import { useState, useEffect } from 'react'
import { JsonObject } from 'type-fest'

import EnvContext from './context'
import getConfig from './configGenerator'
import Loading from './Loading'

type EnvProviderProps<EnvSchema extends JsonObject> = {
  children: ((props: { env: EnvSchema }) => React.ReactNode) | React.ReactNode

  /** EnvProvider can take in a commonConfig object of values that does not change across environments.
   * The configPath prop should be set according to local or hosted development.
   */
  /**
   * Object to deep-merge environment-specific config.json into.
   * @param commonConfig
   * @default '{}'
   */
  commonConfig?: object

  /**
   * Path to app environment file, relative to build output directory. The default prop value matches a custom nginx location providing environment-specific json.
   * @param configPath
   * @default '/app-environment'
   */
  configPath?: '/app-environment' | string

  /**
   * If defined, renders the <LoadingIndicator /> until the config is available.
   * @param loadingIndicator
   * @default <Loading />
   */
  loadingIndicator?: boolean | ComponentType | (() => ReactElement)
  onConfigFailure?: () => void
}

const EnvProvider = <EnvSchema extends JsonObject>({
  children,
  commonConfig = {},
  configPath,
  loadingIndicator,
  onConfigFailure,
}: EnvProviderProps<EnvSchema>) => {
  const [config, setConfig] = useState<EnvSchema | undefined>(undefined)

  useEffect(() => {
    getConfig<EnvSchema>(configPath)
      .then((res) => {
        setConfig(res)
      })
      .catch(onConfigFailure)
  }, [commonConfig, configPath, loadingIndicator, onConfigFailure])

  const mergedConfig = merge(commonConfig, config)

  return (
    <EnvContext.Provider value={mergedConfig}>
      {!config ? (
        loadingIndicator ? (
          <Loading />
        ) : (
          ''
        )
      ) : typeof children === 'function' ? (
        children({ env: mergedConfig })
      ) : (
        children
      )}
    </EnvContext.Provider>
  )
}

export default EnvProvider
